/**
 * LinkedInExportGuideDark
 *
 * A dark-theme, popover-triggered variant of the LinkedInExportGuide used
 * inside the FreeScoreHero card. The popover carries the same GIF walk-
 * through + numbered steps (Open profile → Resources → Save to PDF → drop
 * it in). Kept in a separate file so the Dashboard's light-theme
 * accordion isn't disturbed.
 */
import React, { useState } from 'react';
import { HelpCircle, X } from 'lucide-react';

const GIF_URL =
  'https://customer-assets.emergentagent.com/job_profile-edge-ai/artifacts/2qqf4po4_LinkedIn-PDF-Profile.gif';

const STEPS = [
  {
    n: 1,
    title: 'Open your LinkedIn profile',
    detail: 'Go to linkedin.com and click your face/avatar (top right) \u2192 "View Profile".',
  },
  {
    n: 2,
    title: 'Click the "Resources" button',
    detail: 'Right under your name and headline, click "Resources".',
  },
  {
    n: 3,
    title: 'Choose "Save to PDF"',
    detail: 'From the dropdown pick "Save to PDF". LinkedIn downloads a print-ready PDF automatically.',
  },
  {
    n: 4,
    title: 'Drop the PDF into the box',
    detail: 'Drag-and-drop or click "Choose PDF" \u2014 we extract your headline, About, experiences, skills, education.',
  },
];

export function LinkedInExportGuideDark() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/20 text-sky-700 text-[11.5px] px-2.5 h-7 transition-colors"
        data-testid="free-score-hero-howto-btn"
      >
        <HelpCircle size={12} aria-hidden="true" />
        Where do I find the PDF?
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="free-score-hero-howto-modal"
        >
          <div
            className="relative w-full max-w-[560px] max-h-[92vh] overflow-auto rounded-2xl border border-sky-500/40 bg-[#070f22] p-6 text-slate-100 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/[0.06]"
              aria-label="Close"
              data-testid="free-score-hero-howto-close"
            >
              <X size={16} />
            </button>

            <p className="text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-sky-300">
              Takes ~15 seconds
            </p>
            <h3 className="mt-1 text-xl font-semibold tracking-tight">
              How to export your LinkedIn profile
            </h3>

            <div className="mt-4 rounded-lg border border-sky-500/25 bg-[#0a1330] overflow-hidden">
              <img
                src={GIF_URL}
                alt="Animated walkthrough: exporting a LinkedIn profile to PDF"
                className="w-full h-auto block"
              />
            </div>

            <ol className="mt-5 space-y-3">
              {STEPS.map((s) => (
                <li
                  key={s.n}
                  className="flex gap-3"
                  data-testid={`free-score-hero-howto-step-${s.n}`}
                >
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500 text-[11px] font-mono-edge font-semibold text-white">
                    {s.n}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-medium text-slate-100">{s.title}</p>
                    <p className="text-[12px] text-slate-400 mt-0.5 leading-relaxed">
                      {s.detail}
                    </p>
                  </div>
                </li>
              ))}
            </ol>

            <p className="mt-5 text-[11.5px] text-slate-400 leading-relaxed">
              Your PDF never leaves your session. We extract structured fields and discard the raw file.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export default LinkedInExportGuideDark;
